import React, { useEffect, useRef, useState } from "react";
import { EditorState } from "draft-js";
import { marked } from "marked";
import { stateFromHTML } from "draft-js-import-html";
import { stateToMarkdown } from "draft-js-export-markdown";
import EditorBox from "features/Bookmarks/components/Editor";
import { Box, Checkbox, Divider, Stack, Typography } from "@mui/material";
import { getRegularDate } from "utils/time.util";

export default function TeacherNotesView({
  submission,
  readOnly = true,
  onReviewedChange,
  onSave,
}) {
  const [editorState, setEditorState] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const changingTimeout = useRef(null);

  const notes = submission?.teacherNote_chr || submission?.teacherNote || "";

  const markdownToContentState = (markdown) =>
    stateFromHTML(marked(markdown || ""));

  const getMarkdown = (state) =>
    stateToMarkdown(state.getCurrentContent());

  useEffect(() => {
    if (submission) {
      setEditorState(EditorState.createWithContent(markdownToContentState(notes)));
    }
  }, [submission]);

  const saveNotes = async () => {
    if (readOnly || !editorState || !onSave) return;
    setIsSaving(true);
    try {
      await onSave(getMarkdown(editorState));
    } finally {
      setIsSaving(false);
    }
  };

  const handleContentChange = () => {
    if (changingTimeout.current) clearTimeout(changingTimeout.current);
    changingTimeout.current = setTimeout(saveNotes, 1500);
  };

  if (!submission) {
    return <Typography color="text.secondary">No submission details are available.</Typography>;
  }

  return (
    <Box sx={{ mt: 2, mb: 8 }}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={{ xs: 1, sm: 4 }} divider={<Divider orientation="vertical" flexItem />}>
        <Typography>Folder: <strong>{submission.folderName_chr || "—"}</strong></Typography>
        <Typography>Submitted: <strong>{getRegularDate(submission.submittedDateTime_dtm)}</strong></Typography>
        {submission.lastUpdatedDateTime_dtm && submission.lastUpdatedDateTime_dtm !== submission.submittedDateTime_dtm && (
          <Typography>Updated: <strong>{getRegularDate(submission.lastUpdatedDateTime_dtm)}</strong></Typography>
        )}
        <Stack direction="row" alignItems="center" sx={{ mt: { xs: 1, sm: 0 } }}>
          <Typography>Reviewed</Typography>
          <Checkbox
            checked={Boolean(submission.isReviewed_ysn)}
            disabled={readOnly}
            onChange={(event) => onReviewedChange?.(event.target.checked)}
          />
        </Stack>
      </Stack>
      <Typography variant="h4" mt={5} mb={1}>Teacher&apos;s Notes</Typography>
      <Typography color="text.secondary" mb={2}>
        (These notes are visible to students as read-only)
      </Typography>
      {editorState && (
        <EditorBox
          summaryEditor
          isFocused
          setIsFocused={() => {}}
          editorState={editorState}
          setEditorState={setEditorState}
          handleSave={() => {}}
          handleBlur={() => {}}
          fromSummary
          isBlur
          onEnterSave={false}
          shouldSave={!readOnly}
          showSaveIcon
          placeholder="Add feedback for the student"
          isSaving={isSaving}
          handleContentChange={handleContentChange}
          readOnly={readOnly}
        />
      )}
    </Box>
  );
}
